import { GoogleMap, Marker } from "@react-google-maps/api";
import classNames from "classnames";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTimezoneSelect } from "react-timezone-select";
import { BackButton } from "../../../components/buttons/backButton";
import { Button } from "../../../components/buttons/button";
import { EmptyState } from "../../../components/empty";
import { EventImage } from "../../../components/event/eventImage";
import { Icon } from "../../../components/icon/icon";
import { MiniHeader } from "../../../components/typography/miniHeader";
import { RenderMarkdown } from "../../../components/typography/renderMarkdown";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useGoogleMaps } from "../../../customeHooks/useGoogleMaps";
import { useHoldTicketsMutation } from "../../../redux/features/event/event.api";
import {
  CreatedEvent,
  CreatedTicket,
} from "../../../redux/features/event/event.types";
import { tzOptions } from "../../../utils/constants";
import { formatDate } from "../../../utils/helpers";
import { TicketPurchase } from "../../../utils/types";
import { ChooseTickets } from "./components/chooseTickets";
import { PreviewOrganizerDetails } from "./components/organizerDetails";

export const PreviewStep = ({
  event,
  tickets,
  ticketsPurchase,
  setTicketsPurchase,
  setStep,
  canBuy,
  canShare = true,
  showBack = false,
  doHoldTicket,
  refetchTickets = () => {},
}: {
  event: CreatedEvent;
  tickets: CreatedTicket[];
  ticketsPurchase: TicketPurchase[];
  setTicketsPurchase: (data: TicketPurchase[]) => void;
  canBuy: boolean;
  canShare?: boolean;
  showBack?: boolean;
  setStep: (data: 1 | 2) => void;
  doHoldTicket?: boolean;
  refetchTickets: () => void;
}) => {
  const [sheetOpen, setSheetOpen] = useState<boolean>(false);
  const [placeDetails, setPlaceDetails] = useState<
    google.maps.places.PlaceResult | undefined
  >(undefined);
  const [, setSearchParams] = useSearchParams();

  const { isLoaded: isMapsLoaded, getDetails } = useGoogleMaps();

  const { parseTimezone } = useTimezoneSelect(tzOptions);

  const mapRef = useRef<HTMLDivElement | null>(null);

  const [holdTickets, { isLoading: holdingTickets }] = useHoldTicketsMutation();

  const goToPayment = async () => {
    if (
      doHoldTicket &&
      ticketsPurchase.filter((tp) => tp.ticket.quantityType === "limited")
        .length > 0
    ) {
      const resp = await holdTickets({
        ticketSelections: ticketsPurchase
          .filter((tp) => tp.ticket.quantityType === "limited")
          .map((tp) => {
            return {
              eventId: event._id,
              ticketId: tp.ticket._id,
              quantity: tp.quantity,
            };
          }),
      });

      if (resp.data?.status === "success") {
        refetchTickets();
        setStep(2);
        setSearchParams({
          session: resp.data.data.sessionId,
        });
      }
    } else {
      setStep(2);
    }
  };

  useEffect(() => {
    if (isMapsLoaded && event.placeId) {
      getDetails({
        placeId: event.placeId,
      }).then((resp) => {
        if (typeof resp !== "string") {
          setPlaceDetails(resp);
        }
      });
    }
    // eslint-disable-next-line
  }, [isMapsLoaded]);

  useEffect(() => {
    if (ticketsPurchase.length > 0) {
      setSheetOpen(true);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <div className="flex flex-col gap-3">
      {showBack && (
        <div className="z-[1] mx-auto w-full max-w-[1070px]">
          <BackButton />
        </div>
      )}

      <div className="relative flex w-full animate-fade-in flex-col gap-3 pb-40 md:flex-row md:flex-wrap md:items-start md:justify-center md:gap-10">
        <div className="flex w-full flex-col gap-3 md:max-w-[500px] xl:sticky xl:top-28">
          <BgWrapper>
            <EventImage dimension={472} image={event.image} />
          </BgWrapper>

          <PreviewOrganizerDetails
            event={event}
            className="hidden md:flex"
            canShare={canShare}
            showSocials
          />
        </div>

        <div className="flex w-full flex-col gap-3 text-sm text-grey-950 md:max-w-[530px]">
          <BgWrapper className="flex flex-col gap-3">
            <p className="font-fraunces text-[32px] leading-[100%] tracking-[-0.04em] text-primary">
              {event?.name ?? ""}
            </p>

            <div className="text-sm text-grey-800">
              <RenderMarkdown>{event?.description ?? ""}</RenderMarkdown>
            </div>
          </BgWrapper>

          <BgWrapper className="flex items-center gap-2">
            <p className="flex flex-1 items-center gap-2 font-bold">
              <Icon
                svg="map-pin"
                width={20}
                height={20}
                className="text-primary"
              />
              {event.hideLocation ? event.placeholderLocation : event.location}
            </p>

            {event.placeId &&
            !event.hideLocation &&
            event.type === "physical" ? (
              <button
                type="button"
                className="w-fit text-sm font-bold text-blue"
                onClick={() => {
                  // const googleMapsUrl = `https://www.google.com/maps/place/?q=place_id:${event.placeId}`;
                  // window.open(googleMapsUrl, "_blank");
                  mapRef?.current?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                VIEW MAP
              </button>
            ) : undefined}
          </BgWrapper>

          <BgWrapper>
            <p className="flex items-center gap-2 font-bold">
              <Icon
                svg="calendar"
                width={20}
                height={20}
                className="text-primary"
              />
              {formatDate(event?.startDate, "ddd, MMMM DD, YYYY", true)}{" "}
              {formatDate(event?.startDate) === formatDate(event?.endDate)
                ? ""
                : `- ${formatDate(event?.endDate, "ddd, MMMM DD, YYYY", true)}`}
            </p>
          </BgWrapper>

          <BgWrapper>
            <p className="flex items-center gap-2 font-bold">
              <Icon
                svg="clock"
                width={20}
                height={20}
                className="text-primary"
              />
              {event?.startTime ?? ""} - {event?.endTime ?? ""}
              {event.timezone
                ? ` (${parseTimezone(event.timezone)?.abbrev})`
                : ""}
            </p>
          </BgWrapper>

          <PreviewOrganizerDetails
            event={event}
            className="flex md:hidden"
            canShare={canShare}
            showSocials
          />

          <div
            className={classNames("md:!hidden", "transition-all duration-300", {
              "fixed inset-0 z-[998] bg-black/50": sheetOpen,
              "h-0": !sheetOpen,
            })}
            onClick={() => {
              setSheetOpen(false);
            }}
          />

          {/* SHEET & TICKET SELECTION  */}
          <BgWrapper
            className={classNames(
              "flex flex-col",
              "rounded-b-none md:rounded-b-2xl",
              "fixed bottom-0 left-0 right-0",
              "z-[999] md:z-0",
              "md:static",
              {
                "gap-4": sheetOpen,
              },
            )}
          >
            <MiniHeader
              header="Choose Ticket"
              className={classNames("", "transition-all duration-300", {
                flex: sheetOpen,
                hidden: !sheetOpen,
              })}
            />

            <div
              className={classNames(
                "overflow-hidden",
                "transition-all duration-300",
                {
                  "h-[253px] overflow-y-scroll md:h-fit": sheetOpen,
                  "h-0": !sheetOpen,
                },
              )}
            >
              {(tickets?.length ?? 0) > 0 ? (
                <ChooseTickets
                  tickets={tickets ?? []}
                  ticketsPurchase={ticketsPurchase}
                  setTicketsPurchase={setTicketsPurchase}
                  showControls={true}
                />
              ) : (
                <EmptyState
                  text="No Tickets Available"
                  subtext="There aren't any tickets available for this event. Check back later."
                  image="/assets/images/ticket-glass.png"
                />
              )}
            </div>

            <Button
              text={
                tickets.every(
                  (t) =>
                    t.quantityType === "limited" && t.quantityAvailable <= 0,
                )
                  ? "Sold Out"
                  : sheetOpen
                    ? "Continue"
                    : "Get a ticket"
              }
              type="button"
              onClick={async () => {
                if (sheetOpen) {
                  await goToPayment();
                } else {
                  setSheetOpen(true);
                }
              }}
              loading={holdingTickets}
              disabled={
                (sheetOpen && ticketsPurchase.length === 0) ||
                (sheetOpen && !canBuy) ||
                holdingTickets ||
                tickets.every(
                  (t) =>
                    t.quantityType === "limited" && t.quantityAvailable <= 0,
                )
              }
              className=""
              variation={
                tickets.every(
                  (t) =>
                    t.quantityType === "limited" && t.quantityAvailable <= 0,
                )
                  ? "disabled"
                  : "primary"
              }
            />
          </BgWrapper>

          {event.placeId && event.type === "physical" ? (
            <BgWrapper
              loading={!isMapsLoaded}
              loaderHeight={288}
              className="flex flex-col gap-3"
              ref={mapRef}
            >
              <div className="flex items-center gap-3">
                <p className="rounded-lg border border-grey-100 p-[10px]">
                  <Icon
                    svg="map-pin"
                    width={20}
                    height={20}
                    className="text-grey-700"
                  />
                </p>

                <p className="flex flex-col">
                  <span className="text-base font-bold text-primary">
                    {event.hideLocation
                      ? "Unlock Location After Ticket Purchase"
                      : placeDetails?.name}
                  </span>
                  <span className="text-sm text-grey-600">
                    {event.hideLocation
                      ? event.placeholderLocation
                      : placeDetails?.formatted_address}
                  </span>
                </p>
              </div>
              {isMapsLoaded && (
                <>
                  {event.hideLocation ? (
                    <div className="h-[200px] w-full overflow-hidden rounded-xl">
                      <img
                        alt="maps"
                        src="/assets/images/map-hidden.png"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <GoogleMap
                      zoom={20}
                      center={{
                        lat: Number(event.latitude),
                        lng: Number(event.longitude),
                      }}
                      mapContainerClassName="w-full h-[200px] rounded-[12px]"
                      options={{
                        fullscreenControl: false,
                        mapTypeControl: false,
                        streetViewControl: false,
                        rotateControl: false,
                        scaleControl: false,
                        zoomControl: false,
                        disableDefaultUI: true,
                      }}
                    >
                      <Marker
                        position={{
                          lat: Number(event.latitude),
                          lng: Number(event.longitude),
                        }}
                      />
                    </GoogleMap>
                  )}
                </>
              )}
            </BgWrapper>
          ) : undefined}
        </div>
      </div>
    </div>
  );
};
